import React, { useState, useEffect, useRef } from "react";
import { Plus, Edit, Trash2, Search, X, ChevronDown, ChevronRight, Building, MoreVertical } from "lucide-react";
import { getAllClients } from "../../api/createClientApi";
import {
  createDriverPaySlab,
  getAllDriverPaySlabs,
  updateDriverPaySlab,
  deleteDriverPaySlab
} from "../../api/DriverPaySlabApi";
import { useToast } from "../shared/Toast";
import { showConfirm } from "../shared/ConfirmContext";
import CustomSelect from "../shared/CustomSelect";
import { VEHICLE_TYPES } from "../../constants/vehicleConstants";

const ActionMenu = ({ onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 85;
      const spaceBelow = window.innerHeight - rect.bottom;
      const shouldPositionAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

      setMenuPosition({
        top: shouldPositionAbove ? rect.top - dropdownHeight : rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClose = () => setOpen(false);
    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose);
    return () => {
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose);
    };
  }, [open]);

  return (
    <div style={{ position: "relative", display: "inline-block" }} ref={buttonRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "6px",
          borderRadius: "6px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: open ? "#111827" : "#6B7280",
          backgroundColor: open ? "#F3F4F6" : "transparent",
          transition: "background-color 0.15s ease, color 0.15s ease",
        }}
        onMouseEnter={(e) => {
          if (!open) e.currentTarget.style.backgroundColor = "#F3F4F6";
          e.currentTarget.style.color = "#111827";
        }}
        onMouseLeave={(e) => {
          if (!open) {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "#6B7280";
          }
        }}
        title="Actions"
      >
        <MoreVertical size={16} />
      </button>

      {open && (
        <>
          <div
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            style={{ position: "fixed", inset: 0, zIndex: 999 }}
          />
          <div
            style={{
              position: "fixed",
              top: menuPosition.top,
              right: menuPosition.right,
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)",
              border: "1px solid #E5E7EB",
              minWidth: "120px",
              zIndex: 1000,
              padding: "4px 0",
              overflow: "hidden",
            }}
          >
            <div
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onEdit();
              }}
              style={{
                padding: "8px 14px",
                fontSize: "13px",
                color: "#374151",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "background-color 0.15s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F9FAFB")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <Edit size={14} color="#4B5563" />
              <span>Edit</span>
            </div>
            <div
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onDelete();
              }}
              style={{
                padding: "8px 14px",
                fontSize: "13px",
                color: "#EF4444",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "background-color 0.15s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#FEF2F2")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <Trash2 size={14} color="#EF4444" />
              <span>Delete</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const DriverPaySlab = () => {
  const toast = useToast();
  const [slabs, setSlabs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSlabId, setSelectedSlabId] = useState(null);

  // Accordion State
  const [expandedCompanies, setExpandedCompanies] = useState({});

  const toggleCompany = (clientId) => {
    setExpandedCompanies(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  // Form State
  const [form, setForm] = useState({
    clientId: "",
    vehicleType: "Sedan",
    isRostered: "true",
    rate: "",
    slabType: "fixed",
    startRange: "",
    endRange: ""
  });

  // Dynamic Slab Rows for Bulk Slab Creation
  const [slabList, setSlabList] = useState([
    { startRange: "0", endRange: "", rate: "" }
  ]);

  // Modal Searchable Select States
  const [clientSearch, setClientSearch] = useState("");
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [selectedClientLabel, setSelectedClientLabel] = useState("");

  const handleAddSlabRow = () => {
    const lastRow = slabList[slabList.length - 1];
    const defaultStart = lastRow && lastRow.endRange ? lastRow.endRange : "";
    setSlabList([...slabList, { startRange: defaultStart, endRange: "", rate: "" }]);
  };

  const handleRemoveSlabRow = (index) => {
    if (slabList.length <= 1) return;
    setSlabList(slabList.filter((_, i) => i !== index));
  };

  const handleSlabRowChange = (index, field, value) => {
    const updated = [...slabList];
    updated[index][field] = value;
    setSlabList(updated);
  };

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchSlabs(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Debounced backend search for Client dropdown
  useEffect(() => {
    if (!modalOpen) return;
    const handler = setTimeout(async () => {
      try {
        const res = await getAllClients(1, 50, clientSearch);
        setClients(res.clients || res.data || []);
      } catch (err) {
        console.error("Error searching clients:", err);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [clientSearch, modalOpen]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [slabsRes, clientsRes] = await Promise.all([
        getAllDriverPaySlabs({ search: searchTerm }),
        getAllClients(1, 50, "")
      ]);

      if (slabsRes.success) setSlabs(slabsRes.data || []);
      setClients(clientsRes.clients || clientsRes.data || []);
    } catch (error) {
      console.error("Error loading slabs configuration data", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSlabs = async (searchQuery) => {
    try {
      setLoading(true);
      const slabsRes = await getAllDriverPaySlabs({ search: searchQuery });
      if (slabsRes.success) setSlabs(slabsRes.data || []);
    } catch (error) {
      console.error("Error fetching filtered pay slabs", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setForm({
      clientId: "",
      vehicleType: "Sedan",
      isRostered: "true",
      rate: "",
      slabType: "fixed",
      startRange: "",
      endRange: ""
    });
    setSlabList([{ startRange: "0", endRange: "", rate: "" }]);
    setSelectedClientLabel("");
    setClientSearch("");
    setEditMode(false);
    setSelectedSlabId(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (slab) => {
    setForm({
      clientId: slab.clientId,
      vehicleType: slab.vehicleType || "Sedan",
      isRostered: slab.isRostered !== undefined ? String(slab.isRostered) : "true",
      rate: slab.rate,
      slabType: slab.slabType,
      startRange: slab.startRange || "",
      endRange: slab.endRange || ""
    });
    setSlabList([{ startRange: slab.startRange || "0", endRange: slab.endRange || "", rate: slab.rate || "" }]);

    const foundClient = clients.find((c) => String(c.id) === String(slab.clientId)) || slab.client;
    const cName = foundClient?.clientName || foundClient?.client_name || foundClient?.companyName || foundClient?.name || "Selected Client";
    setSelectedClientLabel(cName);

    setSelectedSlabId(slab.id);
    setEditMode(true);
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clientId || !form.vehicleType) {
      toast("Please fill in Client and Vehicle Type", "info");
      return;
    }

    const isRosteredBool = form.isRostered === "true" || form.isRostered === true;

    let payload;
    if (!editMode) {
      // Validate slab rows
      const validSlabs = slabList.filter(s => s.rate !== "");
      if (validSlabs.length === 0) {
        toast("Please specify at least one valid rate amount", "info");
        return;
      }
      payload = {
        clientId: form.clientId,
        vehicleType: form.vehicleType,
        isRostered: isRosteredBool,
        slabType: form.slabType,
        slabs: validSlabs.map(s => {
          const isSlab = form.slabType === "slab";
          const startNum = parseFloat(s.startRange);
          const endNum = parseFloat(s.endRange);
          return {
            slabType: form.slabType,
            startRange: isSlab ? (!isNaN(startNum) ? startNum : 0) : null,
            endRange: isSlab ? (!isNaN(endNum) ? endNum : null) : null,
            rate: parseFloat(s.rate) || 0
          };
        })
      };
    } else {
      if (!form.rate) {
        toast("Please enter rate", "info");
        return;
      }
      const isSlab = form.slabType === "slab";
      const startNum = parseFloat(form.startRange);
      const endNum = parseFloat(form.endRange);
      payload = {
        clientId: form.clientId,
        vehicleType: form.vehicleType,
        isRostered: isRosteredBool,
        rate: parseFloat(form.rate) || 0,
        slabType: form.slabType,
        startRange: isSlab ? (!isNaN(startNum) ? startNum : 0) : null,
        endRange: isSlab ? (!isNaN(endNum) ? endNum : null) : null
      };
    }

    try {
      let res;
      if (editMode) {
        res = await updateDriverPaySlab(selectedSlabId, payload);
      } else {
        res = await createDriverPaySlab(payload);
      }

      if (res.success) {
        toast(res.message || (editMode ? "Slab updated successfully!" : "Driver pay slab created successfully!"), "success");
        setModalOpen(false);
        fetchSlabs(searchTerm);
      }
    } catch (error) {
      toast("Failed to save pay slab: " + (error.response?.data?.message || error.message), "error");
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm({
      title: "Delete Pay Slab Configuration",
      message: "Are you sure you want to delete this configuration?",
      danger: true,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
    });
    if (!confirmed) return;
    try {
      const res = await deleteDriverPaySlab(id);
      if (res.success) {
        toast("Pay slab deleted successfully", "success");
        fetchSlabs(searchTerm);
      }
    } catch (error) {
      toast("Failed to delete pay slab: " + (error.response?.data?.message || error.message), "error");
    }
  };

  const getGroupedSlabs = () => {
    const grouped = {};
    slabs.forEach(slab => {
      const foundClient = clients.find((c) => String(c.id) === String(slab.clientId));
      const clientObj = slab.client || foundClient;
      const clientName = clientObj?.clientName || clientObj?.client_name || clientObj?.companyName || clientObj?.company_name || clientObj?.name || "N/A";
      const clientId = slab.clientId;

      if (!grouped[clientId]) {
        grouped[clientId] = {
          clientId,
          clientName,
          slabsList: []
        };
      }

      grouped[clientId].slabsList.push(slab);
    });

    return Object.values(grouped);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '12px' }}>
      
      {/* Header and Add Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1F2937' }}>Driver Pay Slabs</h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6B7280' }}>Configure payout rates paid per client company and vehicle type.</p>
        </div>
        <button
          onClick={handleOpenAdd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#227B48',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 18px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            transition: 'all 0.2s'
          }}
        >
          <Plus size={18} /> Add Pay Slab
        </button>
      </div>

      {/* Search Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
          <input
            type="text"
            placeholder="Search by client, vehicle type, rate..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: 'white'
            }}
          />
        </div>
      </div>

      {/* Slabs Accordions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', color: '#666' }}>Loading configurations...</div>
        ) : slabs.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', color: '#888' }}>
            {searchTerm ? "No driver pay slabs match your search." : "No driver pay slabs configured yet."}
          </div>
        ) : (
          getGroupedSlabs().map((cGroup) => {
            const isCompanyExpanded = !!expandedCompanies[cGroup.clientId];

            return (
              <div 
                key={cGroup.clientId}
                style={{ 
                  backgroundColor: 'white', 
                  borderRadius: '12px', 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)', 
                  border: '1px solid #E5E7EB',
                  overflow: 'hidden'
                }}
              >
                {/* Company Accordion Header */}
                <div 
                  onClick={() => toggleCompany(cGroup.clientId)}
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    padding: '18px 24px', 
                    cursor: 'pointer',
                    backgroundColor: '#F9FAFB',
                    borderBottom: isCompanyExpanded ? '1px solid #E5E7EB' : 'none',
                    userSelect: 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#EBF5FF', color: '#1E40AF', display: 'flex', alignItems: 'center' }}>
                      <Building size={20} />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#1F2937' }}>
                        {cGroup.clientName}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#227B48', backgroundColor: '#E8F5EE', padding: '4px 12px', borderRadius: '12px' }}>
                      {cGroup.slabsList.length} rate{cGroup.slabsList.length !== 1 ? 's' : ''}
                    </span>
                    {isCompanyExpanded ? <ChevronDown size={20} color="#6B7280" /> : <ChevronRight size={20} color="#6B7280" />}
                  </div>
                </div>

                {/* Slabs Details Table */}
                {isCompanyExpanded && (
                  <div style={{ overflowX: 'auto', padding: '12px 24px 20px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#FAFBFC', borderBottom: '1px solid #E5E7EB', fontWeight: 600, color: '#4B5563' }}>
                          <th style={{ padding: '10px 16px' }}>Vehicle Type</th>
                          <th style={{ padding: '10px 16px', textAlign: 'center' }}>isRostered</th>
                          <th style={{ padding: '10px 16px', textAlign: 'center' }}>Range (KMs)</th>
                          <th style={{ padding: '10px 16px', textAlign: 'right' }}>Rate Amount</th>
                          <th style={{ padding: '10px 16px', textAlign: 'center', width: '100px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cGroup.slabsList.map((slab) => (
                          <tr key={slab.id} style={{ borderBottom: '1px solid #F9FAFB' }}>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{
                                padding: '3px 8px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: 600,
                                backgroundColor: '#FFFBEB',
                                color: '#D97706',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                {slab.vehicleType || slab.vehicle?.vehicleType || "Standard"}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{
                                padding: '3px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                backgroundColor: (slab.isRostered === true || String(slab.isRostered) === "true") ? '#DCFCE7' : '#F3F4F6',
                                color: (slab.isRostered === true || String(slab.isRostered) === "true") ? '#15803D' : '#6B7280'
                              }}>
                                {(slab.isRostered === true || String(slab.isRostered) === "true") ? "True" : "False"}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', color: '#4B5563' }}>
                              {slab.slabType === "slab"
                                ? `${slab.startRange || 0} - ${slab.endRange || "Infinity"} km`
                                : "Full Route"}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: '#227B48' }}>
                              ₹{Number(slab.rate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <ActionMenu
                                  onEdit={() => handleOpenEdit(slab)}
                                  onDelete={() => handleDelete(slab.id)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add / Edit Modal */}
      {modalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ width: '90%', maxWidth: '500px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
            <div style={{ padding: '16px 24px', backgroundColor: '#227B48', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '16px', fontWeight: 600 }}>
                {editMode ? "Edit Driver Pay Slab" : "Add Driver Pay Slab"}
              </h3>
              <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              {/* Searchable Client Selector */}
              <div style={{ position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#374151' }}>Company (Client) *</label>
                <input
                  type="text"
                  placeholder="Type to search client company..."
                  value={clientDropdownOpen ? clientSearch : (selectedClientLabel || clientSearch)}
                  onFocus={() => {
                    setClientDropdownOpen(true);
                    setClientSearch("");
                  }}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setClientDropdownOpen(true);
                  }}
                  style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                  required={!form.clientId}
                />
                {clientDropdownOpen && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #D1D5DB', borderRadius: '6px', maxHeight: '180px', overflowY: 'auto', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', marginTop: '4px' }}>
                    {clients.length === 0 ? (
                      <div style={{ padding: '10px', fontSize: '13px', color: '#9CA3AF' }}>No clients found</div>
                    ) : (
                      clients.map((c) => {
                        const cName = c.clientName || c.client_name || c.companyName || c.company_name || c.name || "Unknown Client";
                        return (
                          <div
                            key={c.id}
                            onClick={() => {
                              setForm({ ...form, clientId: c.id });
                              setSelectedClientLabel(cName);
                              setClientDropdownOpen(false);
                            }}
                            style={{ padding: '8px 12px', fontSize: '13px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', backgroundColor: String(form.clientId) === String(c.id) ? '#EBF5FF' : 'white' }}
                          >
                            {cName}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* Vehicle Type Selector */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#374151' }}>Vehicle Type *</label>
                <CustomSelect
                  value={form.vehicleType}
                  onChange={(val) => setForm({ ...form, vehicleType: val })}
                  options={VEHICLE_TYPES.map((type) => ({ value: type, label: type }))}
                  placeholder="Select Vehicle Type"
                  searchable
                  width="100%"
                />
              </div>

              {/* isRostered Selector */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#374151' }}>isRostered *</label>
                <CustomSelect
                  value={String(form.isRostered)}
                  onChange={(val) => setForm({ ...form, isRostered: val === 'true' })}
                  options={[
                    { value: 'true', label: 'True' },
                    { value: 'false', label: 'False' },
                  ]}
                  width="100%"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#374151' }}>Slab Type *</label>
                <CustomSelect
                  value={form.slabType}
                  onChange={(val) => setForm({ ...form, slabType: val })}
                  options={[
                    { value: 'fixed', label: 'Fixed Rate (Full Route)' },
                    { value: 'slab', label: 'Km Slab Rate' },
                  ]}
                  width="100%"
                />
              </div>

              {!editMode ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                    {form.slabType === "slab" ? "Km Slab Ranges & Rates *" : "Payout Rates *"}
                  </label>
                  {slabList.map((row, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', backgroundColor: '#F9FAFB', padding: '10px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                      {form.slabType === "slab" && (
                        <>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '2px' }}>Start Km</span>
                            <input
                              type="number"
                              step="0.1"
                              placeholder="0"
                              value={row.startRange}
                              onChange={(e) => handleSlabRowChange(idx, "startRange", e.target.value)}
                              style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '2px' }}>End Km (Optional)</span>
                            <input
                              type="number"
                              step="0.1"
                              placeholder="Max"
                              value={row.endRange}
                              onChange={(e) => handleSlabRowChange(idx, "endRange", e.target.value)}
                              style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }}
                            />
                          </div>
                        </>
                      )}
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '2px' }}>Rate (₹) *</span>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Amount"
                          value={row.rate}
                          onChange={(e) => handleSlabRowChange(idx, "rate", e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }}
                          required
                        />
                      </div>
                      {slabList.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveSlabRow(idx)}
                          style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', marginTop: '14px' }}
                          title="Remove row"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddSlabRow}
                    style={{
                      alignSelf: 'flex-start',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundColor: '#EBF5FF',
                      color: '#1E40AF',
                      border: '1px dashed #93C5FD',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      marginTop: '4px'
                    }}
                  >
                    <Plus size={14} /> {form.slabType === "slab" ? "Add Another Slab Range" : "Add Another Rate"}
                  </button>
                </div>
              ) : (
                <>
                  {form.slabType === "slab" && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#374151' }}>Start Km</label>
                        <input
                          type="number"
                          step="0.1"
                          placeholder="0"
                          value={form.startRange}
                          onChange={(e) => setForm({ ...form, startRange: e.target.value })}
                          style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#374151' }}>End Km (Optional)</label>
                        <input
                          type="number"
                          step="0.1"
                          placeholder="Leave empty for Max"
                          value={form.endRange}
                          onChange={(e) => setForm({ ...form, endRange: e.target.value })}
                          style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#374151' }}>
                      Rate (₹) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Enter rate amount"
                      value={form.rate}
                      onChange={(e) => setForm({ ...form, rate: e.target.value })}
                      style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                      required
                    />
                  </div>
                </>
              )}

              <button
                type="submit"
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#2563EB',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginTop: '12px'
                }}
              >
                {editMode ? "Update Slab" : "Create Slab"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverPaySlab;
